import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthzProvider as SharedAuthzProvider } from '@ohpen/react-authz';
import { useAuth } from '@ohpen/react-auth';
import { PmpConfigContext } from '@pmp/contexts';
import { AuthzContextType, FeatureFlag, Permission } from './interfaces';
import { usersGetCurrentUserPermissions } from '@pmp/api';
import { toPermissionSet } from './utils';

export function AuthzProvider({ children }: { readonly children: React.ReactNode }) {
  const { featureToggles } = useContext(PmpConfigContext);
  const auth = useAuth();
  const [userPermissions, setUserPermissions] = useState<Set<Permission>>(new Set());
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const envFeatureFlags = useMemo(() => {
    const on = Object.entries(featureToggles ?? {})
      .filter(([, v]) => Boolean(v))
      .map(([k]) => k as FeatureFlag);
    return new Set<FeatureFlag>(on);
  }, [featureToggles]);

  const isLoading = useMemo(
    () => auth.isLoading || !permissionsLoaded || featureToggles === undefined,
    [auth.isLoading, permissionsLoaded, featureToggles],
  );

  useEffect(() => {
    // Wait until auth is fully resolved before fetching permissions. Otherwise the
    // effect fires while auth is still loading and briefly yields an empty set.
    if (auth.isLoading) return;

    let cancelled = false;

    const loadUserPermissions = async () => {
      try {
        const { data } = await usersGetCurrentUserPermissions();
        if (!cancelled) setUserPermissions(toPermissionSet(data));
      } catch {
        // On failure treat the user as having no permissions (no-access state).
        if (!cancelled) setUserPermissions(new Set());
      } finally {
        if (!cancelled) setPermissionsLoaded(true);
      }
    };

    loadUserPermissions();

    return () => {
      cancelled = true;
    };
  }, [auth.isLoading]);

  const value: AuthzContextType = useMemo(
    () => ({ userPermissions, envFeatureFlags, isLoading }),
    [userPermissions, envFeatureFlags, isLoading],
  );

  return <SharedAuthzProvider value={value}>{children as any}</SharedAuthzProvider>;
}
